/* eslint-disable import/no-cycle */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import moment from 'moment';
import { generatePath } from 'react-router-dom';
import { AlertSingleLine, Loader, SdirCheckbox, TextLink, Tooltip, styles } from '@sdir/sds';
import { useApi } from '@sdir/httpclient/lib/hooks/useApi';
// eslint-disable-next-line max-len
import { V1ConfirmdraftConfirmowndraftCommandPostRequest } from '@sdir/httpclient/lib/clients/aps/application';
import { ErrorMessage } from '@sdir/utilities/lib/components';
import { ErrorWithTrace } from '@sdir/blueprint.aps/lib/components';
import { translateCertificateType } from '../../../../helpers/Certificates';
import {
  useApplicationSteps,
  useCertificateCodes,
  useCurrentApplication,
  useGetCertificateName,
} from '../../../../services/hooks';
import { Card, CardRow } from '../../../Atoms';
import NewApplicationLayout from '../../NewApplicationLayout';
import AddressOverview from '../../../Atoms/AddressOverview';
import { paymentApi, calculateExpiryApi, confirmDraftApi } from '../../../../httpclient';
import { ArticleInfo } from './ArticleInfo';
import { ToggleContent } from '../../../Atoms/ToggleContent';
import { ToggleDisablePrinting } from '../../../../constants/permissions';
import { DisplayDateFormat } from '../../../../helpers';
import OverruleModal from './OverruleModal';
import { useNavigateWithLang } from '../../../../services/hooks/useNavigateWithLang';
import { ApplicationStatus } from '../../../../types';

const getArticleInfoApiCall = paymentApi?.v1PaymentGetarticleinfoQueryPost?.bind(paymentApi);

export const Summary = () => {
  const intl = useIntl();
  const navigate = useNavigateWithLang();
  const { application, refresh } = useCurrentApplication();
  const certificateName = useGetCertificateName(application.result?.certificate || '');
  const codes = useCertificateCodes();
  const { nextStep } = useApplicationSteps();
  const [showOverrule, setShowOverrule] = useState<boolean>(false);
  const [hasConsent, setHasConsent] = useState<boolean>(false);
  const isSid = ['SID', 'SID_duplicate'].includes(application?.result?.certificate || '');

  const applicationType = useMemo(() => {
    if (!application?.result?.applicationType) return '';
    return translateCertificateType(application.result.applicationType, intl);
  }, [application.result?.applicationType, intl]);

  const { result: expiryResult, callApi: callExpiryApi } = useApi({
    apiFunction: calculateExpiryApi.v1CalculateexpiryCalculateexpiryCommandPost,
    bind: calculateExpiryApi,
  });

  const {
    result: articleInfoResult,
    loading: articleInfoLoading,
    errorObject: articleInfoError,
    callApi: callArticleInfo,
  } = useApi({ apiFunction: getArticleInfoApiCall });

  const {
    loading: isSubmitting,
    errorObject: submitError,
    callApi: submit,
  } = useApi({
    apiFunction: (p) => confirmDraftApi.v1ConfirmdraftConfirmowndraftCommandPost(p),
    successCallback: () => {
      refresh?.();
      if (nextStep) {
        navigate(
          generatePath(nextStep?.route, {
            applicationUid: application?.result?.uid || '',
          })
        );
      }
    },
  });

  const isCertificatePrintable = useMemo(() => {
    const certificate = codes?.result?.find(
      (cert) => cert.code === application.result?.certificate
    );
    return certificate?.isPrintable;
  }, [codes?.result, application.result?.certificate]);

  const expiryDateDisplay = useMemo(() => {
    if (!expiryResult?.expiryDate) return undefined;
    const date = new Date(expiryResult.expiryDate);
    const today = new Date();
    const sixMonthsAhead = new Date(today);
    sixMonthsAhead.setMonth(today.getMonth() + 6);

    if (date?.getTime() < sixMonthsAhead?.getTime()) {
      return DisplayDateFormat(expiryResult.expiryDate, true);
    }
    return undefined;
  }, [expiryResult?.expiryDate]);

  const handleSubmit = useCallback(async () => {
    if (!application.result) return;
    if (application.result.applicationStatus === ApplicationStatus.Draft) {
      const payload: V1ConfirmdraftConfirmowndraftCommandPostRequest = {
        uid: application?.result?.uid || '',
        acceptedTermsAndConditions: !!hasConsent,
        acceptedTermsAndConditionsText: hasConsent
          ? intl.formatMessage({ id: 'summarystep.consent.description' })
          : undefined,
      };
      await submit(payload);
    } else if (application.result.applicationStatus === ApplicationStatus.Payment) {
      if (nextStep) {
        navigate(
          generatePath(nextStep?.route, {
            applicationUid: application?.result?.uid || '',
          })
        );
      }
    }
  }, [application.result, hasConsent, navigate, nextStep, submit]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    if (!application.result) return;

    callArticleInfo({
      certificateCode: application.result.certificate,
      chargeDate: moment().format(),
    });
    callExpiryApi?.({ applicationId: application?.result?.uid || '' });
  }, [application.result, callArticleInfo]);

  return (
    <NewApplicationLayout
      exitLabel={intl.formatMessage({ id: 'general.form.back' })}
      mainContentMaxWidth="100%"
      canNavBack
      canNavNext={!!application.result && !isSubmitting && hasConsent}
      navBackLabel={intl.formatMessage({ id: 'general.form.cancel' })}
      navNextLabel={intl.formatMessage({ id: 'go.to.payment.button' })}
      onNavNextClick={handleSubmit}
      onNavBackClick={handleBack}
    >
      {application.loading && <Loader />}
      {application.error && <ErrorMessage error={application.error} />}
      {application.result && (
        <>
          <StyledCard heading={intl.formatMessage({ id: 'newapp.steps.summary' })}>
            <CardRow
              label={intl.formatMessage({
                id: 'certificate.type',
              })}
            >
              {certificateName}
            </CardRow>
            <CardRow
              label={intl.formatMessage({
                id: 'certificate.app.type',
              })}
            >
              {applicationType}
            </CardRow>
            {expiryDateDisplay && (
              <>
                <CardRow label={intl.formatMessage({ id: 'summarystep.expirydate' })}>
                  <ExpiryDateValue>
                    {expiryDateDisplay}
                    <TextLink
                      text={intl.formatMessage({ id: 'summarystep.expirydate.overrule' })}
                      onClick={() => setShowOverrule(true)}
                    />
                  </ExpiryDateValue>
                  <ExpiryDateInfo>
                    <span>
                      {intl.formatMessage({ id: 'summarystep.expirydate.disagree.header' })}
                    </span>
                    <Tooltip
                      small
                      width="35rem"
                      placement="bottom-left"
                      ariaLabelToggle={intl.formatMessage({ id: 'summarystep.expirydate.info' })}
                    >
                      <span>
                        {intl.formatMessage({ id: 'summarystep.expirydate.disagree.info' })}
                      </span>
                    </Tooltip>
                  </ExpiryDateInfo>
                </CardRow>
                <OverruleModal
                  application={application?.result}
                  show={showOverrule}
                  onClose={() => setShowOverrule(false)}
                />
              </>
            )}
            {isCertificatePrintable && (
              <ToggleContent toggleName={ToggleDisablePrinting} showLoader inverse>
                <CardRow
                  label={intl.formatMessage({ id: 'physical.certificate' })}
                  className="standardText"
                >
                  {application.result.printCertificate
                    ? intl.formatMessage({ id: 'physical.certificate.required' })
                    : intl.formatMessage({ id: 'physical.certificate.notrequired' })}
                </CardRow>
              </ToggleContent>
            )}
            <AddressOverview shipmentAddress={application.result.shipmentAddress} />
            {articleInfoLoading && <Loader />}
            {articleInfoError && <ErrorMessage error={articleInfoError} />}
            {articleInfoResult && <ArticleInfo article={articleInfoResult} />}
          </StyledCard>
          <Consent>
            <SdirCheckbox
              checked={hasConsent}
              title={
                <FormattedMessage
                  id={isSid ? 'summarystep.consent.sid.title' : 'summarystep.consent.title'}
                  tagName="span"
                  values={{
                    b: (chunks) => <b>{chunks}</b>,
                  }}
                />
              }
              onClick={() => setHasConsent(!hasConsent)}
            />
            <Tooltip width="42rem" placement="top-left">
              {intl.formatMessage({ id: 'summarystep.consent.description' })}
            </Tooltip>
          </Consent>
        </>
      )}
      {submitError && (
        <FeedbackWrapper>
          <ErrorWithTrace
            error={submitError?.response?.data?.message || submitError}
            trace={submitError?.response?.headers?.traceresponse}
          />
        </FeedbackWrapper>
      )}
      {isSubmitting && (
        <FeedbackWrapper>
          <Loader />
        </FeedbackWrapper>
      )}
      {application.result && hasConsent && (
        <AlertSingleLine
          title="Info"
          type="info"
          text={intl.formatMessage({ id: 'summarystep.payment.info' })}
        />
      )}
    </NewApplicationLayout>
  );
};

const StyledCard = styled(Card)`
  .standardText {
    .cardValue__content {
      font-family: inherit;
      display: flex;
      column-gap: 8px;
    }
  }
  margin-bottom: 32px;
`;

const ExpiryDateValue = styled.div`
  display: flex;
  & > button {
    margin-left: 10px;
  }
`;

const ExpiryDateInfo = styled.div`
  display: flex;
  align-items: center;
  & button {
    margin-left: 10px;
  }
  &&& span {
    font-family: ${styles.fonts.stfBook};
  }
`;

const FeedbackWrapper = styled.div`
  margin: 20px 0;
`;

const Consent = styled.div`
  margin: 30px 0;
  display: flex;

  @media screen and (max-width: ${({ theme }) => theme.screen.medium}) {
    width: 100%;
  }
`;
